import React from 'react';
import {
  Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, Box, Typography, LinearProgress
} from '@mui/material';
import { User } from '../../models/user';
import { UserPost } from '../../userFetch';

interface BulkAssignDialogProps {
  open: boolean;
  users: User[];
  selectedDevices: string[];
  onClose: () => void;
  onComplete: () => void;
}

function BulkAssignDialog({ open, users, selectedDevices, onClose, onComplete }: BulkAssignDialogProps) {
  const [selectedUser, setSelectedUser] = React.useState('');
  const [view, setView] = React.useState(true);
  const [command, setCommand] = React.useState(false);
  const [configuration, setConfiguration] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  const handleSubmit = async () => {
    if (!selectedUser) return;

    setIsSubmitting(true);
    setProgress(0);

    const total = selectedDevices.length;
    let completed = 0;

    for (const serial of selectedDevices) {
      try {
        await UserPost(`/api/devices/${serial}/permissions`, {
          user_id: selectedUser,
          view,
          command,
          configuration
        });
      } catch (err) {
        console.error(`Failed to set permissions for ${serial}:`, err);
      }
      completed++;
      setProgress((completed / total) * 100);
    }

    setIsSubmitting(false);
    resetForm();
    onComplete();
    onClose();
  };

  const resetForm = () => {
    setSelectedUser('');
    setView(true);
    setCommand(false);
    setConfiguration(false);
    setProgress(0);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Assign Permissions to {selectedDevices.length} Device{selectedDevices.length > 1 ? 's' : ''}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>User</InputLabel>
          <Select
            value={selectedUser}
            label="User"
            onChange={(e) => setSelectedUser(e.target.value)}
            disabled={isSubmitting}
          >
            {users.filter(u => !u.super_admin).map((user) => (
              <MenuItem key={user.uuid} value={user.uuid}>
                {user.email}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Permissions:</Typography>
          <FormControlLabel
            control={<Checkbox checked={view} onChange={(e) => setView(e.target.checked)} disabled={isSubmitting} />}
            label="View"
          />
          <FormControlLabel
            control={<Checkbox checked={command} onChange={(e) => setCommand(e.target.checked)} disabled={isSubmitting} />}
            label="Command"
          />
          <FormControlLabel
            control={<Checkbox checked={configuration} onChange={(e) => setConfiguration(e.target.checked)} disabled={isSubmitting} />}
            label="Configuration"
          />
        </Box>

        {isSubmitting && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
              Processing... {Math.round(progress)}%
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedUser || isSubmitting}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default BulkAssignDialog;
